/**
 * @fileoverview Page `/design-system` — vitrine live du design system GNPC.
 *
 * Cette page N'EST PAS un simple showcase : c'est la source de vérité
 * visuelle utilisée par les designers et les développeurs pour vérifier
 * qu'un token / composant se comporte comme attendu, dans TOUS ses états.
 * Chaque item du catalogue `docs/design-system-catalog.md` livré doit
 * apparaître ici dans sa propre section.
 *
 * La navigation entre sections vit dans `DesignSystemLayout` (sidebar
 * dédiée). Cette page ne s'occupe que du contenu.
 *
 * Ajouter une section vivante :
 *   1. Créer `apps/console/src/features/design-system/sections/DsSectionXxx.tsx`.
 *   2. L'exporter depuis `sections/index.ts`.
 *   3. Basculer `component: DsSectionXxx` dans `DS_SECTIONS`
 *      (`sections/sections-registry.ts`).
 *
 * @see docs/design-system-catalog.md
 */

import { DESIGN_SYSTEM_VERSION } from '@gnpc/ui';
import { DS_SECTIONS } from '../features/design-system/sections/sections-registry.ts';

export function DesignSystemPage(): JSX.Element {
  return (
    <div className="mx-auto max-w-5xl">
      <header className="mb-8">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-quipux-orange-600 text-sm font-medium">Design system</p>
          <span className="border-quipux-orange-200 bg-quipux-orange-50 text-quipux-orange-700 rounded-full border px-2.5 py-0.5 font-mono text-xs font-medium">
            v{DESIGN_SYSTEM_VERSION.version}
          </span>
        </div>
        <h1 className="text-quipux-navy-950 mt-1 text-3xl font-semibold tracking-tight">
          Vitrine du design system GNPC
        </h1>
        <p className="text-muted-foreground mt-2 max-w-3xl">
          Fondations et composants du monorepo <code className="font-mono text-xs">@gnpc/ui</code>.
          Cette page est la source de vérité visuelle : un composant qui n'apparaît pas ici n'existe
          pas encore. La navigation entre sections est dans la sidebar à gauche.
        </p>
      </header>

      <div className="space-y-12">
        {DS_SECTIONS.map((section) => {
          const Component = section.component;
          return (
            <section key={section.id} id={section.id} className="scroll-mt-8">
              <header className="border-border mb-4 border-b pb-2">
                <h2 className="text-quipux-navy-950 text-2xl font-semibold">{section.title}</h2>
                <p className="text-muted-foreground mt-1 text-sm">{section.description}</p>
              </header>
              {Component ? (
                <Component />
              ) : (
                <div className="border-border bg-card text-muted-foreground rounded-lg border border-dashed p-6 text-sm">
                  À livrer — voir{' '}
                  <code className="font-mono text-xs">docs/design-system-catalog.md</code>.
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
